import api from './api';
import type { ChatMode } from './api';
import {
  CourseMaterial,
  Exam,
  ExamQuestion,
  ExamHint,
  ExamSolution,
  ExamPracticeSession,
  CreateExamRequest,
  CourseMaterialUploadRequest,
  ProcessExamRequest,
  ExamChatRequest,
  ExamChatResponse,
  StudyGuideChatRequest,
  StudyGuideChatResponse,
  CourseMaterialResponse,
  ExamResponse,
  ExamQuestionResponse,
  ExamHintResponse,
  ExamSolutionResponse,
  ExamPracticeSessionResponse,
  StudyGuideResponse,
} from '../types/exam.types';

class ExamApiService {
  private async request<T>(
    endpoint: string,
    options: any = {}
  ): Promise<T> {
    const response = await api({
      url: endpoint,
      method: options.method || 'GET',
      data: options.data,
      ...options,
    });

    return response.data;
  }

  private async uploadFile(
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    const response = await api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  // Course Materials API
  async uploadCourseMaterial(
    courseId: number,
    file: File,
    materialType: string
  ): Promise<CourseMaterialResponse> {
    return this.uploadFile(
      `/api/courses/${courseId}/materials`,
      file,
      { material_type: materialType }
    );
  }

  async getCourseMaterials(courseId: number): Promise<CourseMaterial[]> {
    return this.request<CourseMaterial[]>(`/api/courses/${courseId}/materials`);
  }

  async deleteCourseMaterial(materialId: number): Promise<void> {
    return this.request<void>(`/api/materials/${materialId}`, {
      method: 'DELETE',
    });
  }

  // AI Exam Generation API
  async generateAIExamWithFiles(
    courseId: number,
    files: File[],
    examData: {
      title: string;
      questionCount: number;
      difficultyLevel: string;
      guidance?: string;
    }
  ): Promise<ExamResponse> {
    const formData = new FormData();
    
    // Append files
    files.forEach(file => {
      formData.append('files', file);
    });
    
    // Append exam data
    formData.append('title', examData.title);
    formData.append('question_count', examData.questionCount.toString());
    formData.append('difficulty_level', examData.difficultyLevel);
    if (examData.guidance) {
      formData.append('guidance', examData.guidance);
    }
    
    const response = await api.post(`/api/courses/${courseId}/exams/generate-ai`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  // Exam Management API
  async createExam(
    courseId: number,
    examData: CreateExamRequest,
    file?: File
  ): Promise<ExamResponse> {
    if (file) {
      // When uploading a file, use the upload endpoint with form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Append individual fields
      formData.append('title', examData.title);
      formData.append('description', examData.description || '');
      formData.append('exam_type', examData.exam_type || 'midterm');
      if (examData.academic_year) formData.append('academic_year', examData.academic_year);
      if (examData.semester) formData.append('semester', examData.semester);
      if (examData.professor_name) formData.append('professor_name', examData.professor_name);
      if (examData.exam_date) formData.append('exam_date', examData.exam_date);
      if (examData.duration_minutes) formData.append('duration_minutes', examData.duration_minutes.toString());
      if (examData.custom_instructions) formData.append('custom_instructions', examData.custom_instructions);
      
      const response = await api.post(`/api/courses/${courseId}/exams/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } else {
      // When no file, use the regular endpoint with JSON
      return this.request<ExamResponse>(`/api/courses/${courseId}/exams`, {
        method: 'POST',
        data: examData,
      });
    }
  }

  async getCourseExams(courseId: number): Promise<Exam[]> {
    return this.request<Exam[]>(`/api/courses/${courseId}/exams`);
  }

  async getExam(examId: number): Promise<ExamResponse> {
    return this.request<ExamResponse>(`/api/exams/${examId}`);
  }

  async updateExam(examId: number, examData: { title?: string; description?: string }): Promise<ExamResponse> {
    return this.request<ExamResponse>(`/api/exams/${examId}`, {
      method: 'PUT',
      body: JSON.stringify(examData),
    });
  }

  async deleteExam(examId: number): Promise<void> {
    return this.request<void>(`/api/exams/${examId}`, {
      method: 'DELETE',
    });
  }

  // Exam Processing API
  async processExam(examId: number, customInstructions?: string): Promise<{ message: string; status: string }> {
    return this.request<{ message: string; status: string }>(`/api/exams/${examId}/process`, {
      method: 'POST',
      data: {
        custom_instructions: customInstructions
      },
    });
  }

  // AI Exam Generation API
  async generateExam(
    courseId: number,
    title: string,
    sourceExamIds: number[],
    questionCount: number = 3,
    difficultyLevel: string = 'medium',
    guidance?: string
  ): Promise<{ message: string; exam_id: number }> {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('source_exam_ids', sourceExamIds.join(','));
    formData.append('question_count', questionCount.toString());
    formData.append('difficulty_level', difficultyLevel);
    if (guidance) {
      formData.append('guidance', guidance);
    }
    
    const response = await api.post(`/api/courses/${courseId}/exams/generate`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  async generateAnotherAIExam(
    examId: number,
    payload: {
      title?: string;
    } = {}
  ): Promise<ExamResponse> {
    return this.request<ExamResponse>(`/api/exams/${examId}/generate-another-ai`, {
      method: 'POST',
      data: payload,
    });
  }

  async getExamQuestions(examId: number): Promise<ExamQuestion[]> {
    return this.request<ExamQuestion[]>(`/api/exams/${examId}/questions`);
  }

  async getQuestion(questionId: number): Promise<ExamQuestionResponse> {
    return this.request<ExamQuestionResponse>(`/api/questions/${questionId}`);
  }

  // Hints and Solutions API
  async getQuestionHints(questionId: number): Promise<ExamHint[]> {
    return this.request<ExamHint[]>(`/api/questions/${questionId}/hints`);
  }

  async getQuestionSolution(questionId: number): Promise<ExamSolutionResponse> {
    return this.request<ExamSolutionResponse>(`/api/questions/${questionId}/solution`);
  }

  // Practice Session API
  async startPracticeSession(examId: number): Promise<ExamPracticeSessionResponse> {
    return this.request<ExamPracticeSessionResponse>(`/api/exams/${examId}/practice/start`, {
      method: 'POST',
    });
  }

  async getPracticeSession(sessionId: number): Promise<ExamPracticeSessionResponse> {
    return this.request<ExamPracticeSessionResponse>(`/api/practice-sessions/${sessionId}`);
  }

  async nextQuestion(sessionId: number): Promise<ExamPracticeSessionResponse> {
    return this.request<ExamPracticeSessionResponse>(`/api/practice-sessions/${sessionId}/next`, {
      method: 'POST',
    });
  }

  async completeSession(sessionId: number): Promise<ExamPracticeSessionResponse> {
    return this.request<ExamPracticeSessionResponse>(`/api/practice-sessions/${sessionId}/complete`, {
      method: 'POST',
    });
  }

  async setQuestionCompletion(
    sessionId: number,
    questionId: number,
    completed: boolean
  ): Promise<ExamPracticeSessionResponse> {
    return this.request<ExamPracticeSessionResponse>(
      `/api/practice-sessions/${sessionId}/questions/${questionId}/completion`,
      {
        method: 'POST',
        data: { completed },
      }
    );
  }

  // Chat API
  async chatWithExam(
    examId: number,
    message: string,
    context: any = undefined,
    practiceSessionId: number | undefined = undefined,
    mode: ChatMode
  ): Promise<ExamChatResponse> {
    return this.request<ExamChatResponse>('/api/exams/chat', {
      method: 'POST',
      data: {
        exam_id: examId,
        message: message,
        context: context,
        practice_session_id: practiceSessionId,
        mode,
      },
    });
  }

  // Chat History API
  async getExamChatHistory(
    examId: number,
    mode?: ChatMode
  ): Promise<{history: Array<{type: 'user' | 'ai', message: string}>, exam_id: number, user_id: number, mode?: ChatMode}> {
    const url = `/api/exams/${examId}/chat/history`;
    return this.request<{history: Array<{type: 'user' | 'ai', message: string}>, exam_id: number, user_id: number, mode?: ChatMode}>(url, {
      params: mode ? { mode } : undefined,
    });
  }

  // Study Guide Chat API
  async chatWithStudyGuide(
    courseId: number,
    message: string,
    currentTopicId?: string
  ): Promise<StudyGuideChatResponse> {
    return this.request<StudyGuideChatResponse>('/api/study-guides/chat', {
      method: 'POST',
      body: JSON.stringify({
        course_id: courseId,
        message: message,
        current_topic_id: currentTopicId,
      }),
    });
  }

  // Study Guide Chat History API
  async getStudyGuideChatHistory(
    courseId: number
  ): Promise<{history: Array<{type: 'user' | 'ai', message: string}>, course_id: number, user_id: number}> {
    const url = `/api/study-guides/${courseId}/chat/history`;
    return this.request<{history: Array<{type: 'user' | 'ai', message: string}>, course_id: number, user_id: number}>(url);
  }

  // PDF Export API
  async exportExamToPdf(examId: number): Promise<{blob: Blob, filename: string}> {
    const response = await api.get(`/api/exams/${examId}/export-pdf`, {
      responseType: 'blob',
    });

    // Get filename from response headers
    const contentDisposition = response.headers['content-disposition'];
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || contentDisposition.split("filename*=UTF-8''")[1]
      : `StudyBel_Exam_${examId}.pdf`;

    return { blob: response.data, filename };
  }

  async generateStudyGuide(examId: number): Promise<StudyGuideResponse> {
    return this.request<StudyGuideResponse>(`/api/exam/${examId}/study-guide`, {
      method: 'POST',
    });
  }

  async generateCourseStudyGuide(courseId: number): Promise<StudyGuideResponse> {
    return this.request<StudyGuideResponse>(`/api/course/${courseId}/study-guide`, {
      method: 'POST',
    });
  }

  async getCourseStudyGuideStatus(courseId: number): Promise<{
    status: 'not_found' | 'processing' | 'completed' | 'failed';
    message?: string;
    topics?: any[];
  }> {
    return this.request<{
      status: 'not_found' | 'processing' | 'completed' | 'failed';
      message?: string;
      topics?: any[];
    }>(`/api/course/${courseId}/study-guide/status`);
  }

  async deleteCourseStudyGuide(courseId: number): Promise<{message: string}> {
    return this.request<{message: string}>(`/api/course/${courseId}/study-guide`, {
      method: 'DELETE',
    });
  }
}

export const examApi = new ExamApiService();
export default examApi; 
